import { useEffect, useState } from 'react'
import { useI18n } from '../i18n'
import { displayNick, formatTime, type RoomClient, type RoomState } from './client'

/** Viewer requests shown to the owner and controllers. */
export function Requests({ client, state }: { client: RoomClient; state: RoomState }) {
  const { t } = useI18n()
  const canControl = state.you?.role === 'owner' || state.you?.role === 'controller'
  if (!canControl || state.requests.length === 0) return null
  return (
    <div className="requests" aria-live="polite">
      {state.requests.map((r) => (
        <div key={r.id} className="request">
          <p>
            <strong>{displayNick(r.nick)}</strong>{' '}
            {r.kind === 'pause'
              ? t('request.pause')
              : r.kind === 'play'
                ? t('request.play')
                : t('request.seek', { time: formatTime(r.pos ?? 0) })}
          </p>
          <div className="request__actions">
            <button type="button" className="btn btn--primary btn--small" onClick={() => client.answerRequest(r.id, true)}>
              {t('request.accept')}
            </button>
            <button type="button" className="btn btn--small" onClick={() => client.answerRequest(r.id, false)}>
              {t('request.decline')}
            </button>
          </div>
        </div>
      ))}
    </div>
  )
}

/** Short-lived notice at the bottom of the screen. */
export function Notice({ state }: { state: RoomState }) {
  const { t } = useI18n()
  const [visible, setVisible] = useState<RoomState['notice'] | null>(null)
  useEffect(() => {
    if (!state.notice) return
    setVisible(state.notice)
    // Long notices stay longer, to be read to the end.
    const text = t(state.notice.text.key, state.notice.text.vars)
    const timer = window.setTimeout(() => setVisible(null), Math.max(3500, text.length * 60))
    return () => window.clearTimeout(timer)
  }, [state.notice, t])
  if (!visible) return null
  return (
    <div className="notice" role="status" key={visible.key}>
      {t(visible.text.key, visible.text.vars)}
    </div>
  )
}
